import { Outlet } from 'react-router-dom';
import { ResourcesList } from '@dispatch/web/features/resources';
import {
  useGetCountyOptionsWithState,
  useGetResourcesPaginated,
  usePermissions,
  useGetResourceFilters,
  useUpsertResourceListFilter,
  useDeleteResourceFilter,
  useCompany,
  useGetCompanyRoleOptions,
  useFiltersOrderIndexBulkResourceFilterUpdate,
  UseGroupsOrderIndexBulkResourceFiltersUpdate,
  useUpsertResourceFilterGroup,
  useDeleteResourceFilterGroup,
  useGetResourceFilterGroups,
  useGetGroupAttributes,
} from '@dispatch/shared/hooks';
import {
  AttributeDefinition,
  AttributeDefinitionValue,
  AttributeOptions,
  MultiSelectOption,
  Office,
  ResourceAttribute,
  ResourceFilterGroup,
  ResourcesListFilter,
  Team,
} from '@dispatch/shared/types';
import React, { useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import { GridSortModel } from '@mui/x-data-grid-pro';
import { useCurrentUser } from '@dispatch/web/auth';
import { useNotification } from '@dispatch/web/hooks';
import { useGeneralDataContext } from '@dispatch/shared/hooks';

const ResourcesPage = () => {
  const defaultFilters: ResourcesListFilter = {
    pageSize: 10,
  };
  const [filters, setFilters] = useState<ResourcesListFilter>(defaultFilters);

  const { tenantId, userId } = useCurrentUser();

  const { permissions } = usePermissions();
  const [sorting, setSorting] = useState<GridSortModel[number] | null>();

  const [debouncedFilters, setDebouncedFilters] = React.useState<{
    filters: ResourcesListFilter;
    sorting?: GridSortModel[number] | null;
  }>({ filters: {} as ResourcesListFilter, sorting: null });

  useDebounce(() => setDebouncedFilters({ filters, sorting }), 500, [
    filters,
    sorting,
  ]);

  const resources = useGetResourcesPaginated({
    filters: debouncedFilters.filters,
    sorting: debouncedFilters.sorting,
  });
  const { company, teams, offices, groups } = useCompany();
  const attributeDefinitionValues = (values: AttributeDefinitionValue[]) => {
    return values.map((definitionValue: AttributeDefinitionValue) => {
      return {
        value: definitionValue.displayName,
        title: definitionValue.displayName,
        definitionValue,
      };
    });
  };

  const companyAttributes: AttributeOptions[] | undefined =
    company?.attributeDefinition?.flatMap((definition: AttributeDefinition) => {
      if (!definition.isActive) return [];
      const type: 'multi-select' | 'bool' =
        definition?.attributeDefinitionValues.length > 0
          ? 'multi-select'
          : 'bool';
      return {
        value: definition?.displayName,
        title: definition?.displayName,
        valueType: type,
        attributeDefinitionValues: attributeDefinitionValues(
          definition.attributeDefinitionValues
        ),
        definition,
      };
    });

  const { groupAttributesData } = useGetGroupAttributes(tenantId, !!tenantId);

  // Attribute-group options for the grouped-attribute filter dimension.
  // API returns `{ id, attributeGroupName }` -> `{ value: id, title: name }`.
  const attributeGroupOptions: MultiSelectOption[] = useMemo(
    () =>
      (groupAttributesData || []).map(
        (group: { id: string; attributeGroupName: string }) => ({
          value: group.id,
          title: group.attributeGroupName,
        })
      ),
    [groupAttributesData]
  );

  const { statesOptions } = useGeneralDataContext();

  const [rsrcCountyFilterDebouncedValue, setRsrcCountyFilterDebouncedValue] =
    useState('');
  const [rsrcCountyFilterInput, setRsrcCountyFilterInput] =
    useState<string>('');
  const [rsrcStateFilterInput, setRsrcStateFilterInput] = useState<string[]>(
    []
  );
  const { storedFilters, fetchStoredFilters } = useGetResourceFilters(
    userId || ''
  );
  const { storedFilterGroups, fetchStoredFilterGroups } =
    useGetResourceFilterGroups(userId || '');

  const storedFiltersWithGroups = React.useMemo(() => {
    if (storedFilters && storedFilterGroups) {
      return [...storedFilterGroups, ...storedFilters];
    }
    return [];
  }, [storedFilters, storedFilterGroups]);

  const { notifyError, notifySuccess } = useNotification();

  const onResourceFilterDelete = () => {
    notifySuccess('Filter Deleted', 'Filter has been successfully deleted');
    fetchStoredFilters?.();
  };

  const { deleteResourceFilter } = useDeleteResourceFilter(
    userId || '',
    onResourceFilterDelete
  );

  const onComplete = (response: { success: boolean }) => {
    if (response?.success) {
      notifySuccess('Filter Saved', 'Filter has been successfully saved');
      fetchStoredFilters?.();
    } else {
      notifyError('Error', 'Filter was not saved');
    }
  };

  const { upsertResourceFilters } = useUpsertResourceListFilter(
    onComplete,
    userId || ''
  );

  const handleResourceAttributeFilters = (
    currentFilters: ResourcesListFilter
  ) => {
    let resourceAttributes = currentFilters?.resourceAttributes;

    if (typeof resourceAttributes === 'string') {
      resourceAttributes = JSON.parse(
        resourceAttributes
      ) as ResourceAttribute[];
    }

    const attributeFiltersWithValue = companyAttributes?.filter(
      (attribute) => currentFilters?.[attribute.value]
    );

    // needed as typescript doesn't recognize it will always be a ResourceAttribute object and not a string
    const attributes = resourceAttributes as ResourceAttribute[];

    attributeFiltersWithValue?.forEach((attribute) => {
      attributes?.push({
        attributeDefinition: attribute.title,
        // need to parse here otherwise we get too many '/' when stringify
        values: JSON.parse(currentFilters?.[attribute.title] as string),
      });
    });

    if (attributes && attributes.length > 0) {
      currentFilters.resourceAttributes = JSON.stringify(attributes);
    }
  };

  const handleSaveFilters = (values: ResourcesListFilter) => {
    handleResourceAttributeFilters(values);
    upsertResourceFilters(values);
  };

  const { companyRoles } = useGetCompanyRoleOptions(company.role);

  const teamsOptions = teams?.map((team: Team): MultiSelectOption => {
    return {
      title: team.name,
      value: team.integrationGUID,
    };
  });

  const groupOptions = groups?.map((group): MultiSelectOption => {
    return {
      title: group.name,
      value: group.integrationGUID,
    };
  });

  const officeOptions = offices?.map((office: Office): MultiSelectOption => {
    return {
      title: office.name,
      value: office.integrationGUID,
    };
  });

  const filteredRsrcCounties = useGetCountyOptionsWithState(
    rsrcCountyFilterDebouncedValue,
    !!rsrcCountyFilterDebouncedValue,
    rsrcStateFilterInput
  );

  useDebounce(
    () => {
      setRsrcCountyFilterDebouncedValue(rsrcCountyFilterInput);
    },
    1000,
    [rsrcCountyFilterInput]
  );

  const mappedResources = useMemo(() => {
    return {
      ...resources,
      data: resources?.data?.map((r) => {
        return {
          ...r,
          company,
        };
      }),
    };
  }, [resources, company]);

  const onSearch = (value: string) => {
    setFilters({ ...(filters || defaultFilters), name: value });
  };

  const handleSort = ([value]: GridSortModel) => {
    setSorting(value);
    resources.goToFirstPage();
  };

  const onTaskFilterGroupReorder = () => {
    notifySuccess(
      'Filters Reordered',
      'Filters have been successfully reordered'
    );
    fetchStoredFilterGroups?.();
    fetchStoredFilters?.();
  };

  const onTaskFilterGroupDelete = () => {
    notifySuccess(
      'Filter Group Deleted',
      'Filter Group has been successfully deleted'
    );
    fetchStoredFilterGroups?.();
    fetchStoredFilters?.();
  };

  const { orderIndexBulkUpdate } = useFiltersOrderIndexBulkResourceFilterUpdate(
    onTaskFilterGroupReorder,
    userId || ''
  );

  const { groupsOrderIndexBulkUpdate } =
    UseGroupsOrderIndexBulkResourceFiltersUpdate(
      onTaskFilterGroupReorder,
      userId || ''
    );

  const { upsertTaskFilterGroup } = useUpsertResourceFilterGroup(
    onComplete,
    userId || ''
  );

  const { deleteTaskFilterGroup } = useDeleteResourceFilterGroup(
    userId || '',
    onTaskFilterGroupDelete
  );

  const filterOrderIndexBulkUpdate = (
    values: (Omit<ResourcesListFilter, 'pageSize'> & { isNew?: boolean })[]
  ) => {
    orderIndexBulkUpdate(values);
  };

  const handleSaveFilterGroup = (values: ResourceFilterGroup) => {
    upsertTaskFilterGroup(values);
  };

  const groupsOrderIndexBulkUpdateHandler = (values: ResourceFilterGroup[]) => {
    groupsOrderIndexBulkUpdate(values);
  };

  const handleDeleteFilterGroup = (groupId: string) => {
    deleteTaskFilterGroup(groupId);
  };

  return (
    <>
      <ResourcesList
        data={mappedResources}
        onFilterChange={setFilters}
        onSearch={onSearch}
        filters={filters || defaultFilters}
        permissions={permissions}
        handleSort={handleSort}
        companyAttributes={companyAttributes || []}
        attributeGroupOptions={attributeGroupOptions}
        filterOptions={{
          states: statesOptions,
          offices: officeOptions || [],
          roles: companyRoles || [],
          teams: teamsOptions || [],
          groups: groupOptions || [],
        }}
        filteredRsrcCounties={filteredRsrcCounties?.data || []}
        filteredRsrcCountiesStatus={filteredRsrcCounties?.status}
        setRsrcCountyFilterInput={setRsrcCountyFilterInput}
        setRsrcStateFilterInput={setRsrcStateFilterInput}
        handleDeleteFilter={deleteResourceFilter}
        handleSaveFilters={handleSaveFilters}
        storedFilters={storedFiltersWithGroups || []}
        handleOrderIndexBulkUpdate={filterOrderIndexBulkUpdate}
        handleGroupsOrderIndexBulkUpdate={groupsOrderIndexBulkUpdateHandler}
        handleSaveFilterGroup={handleSaveFilterGroup}
        handleDeleteFilterGroup={handleDeleteFilterGroup}
      />
      <Outlet />
    </>
  );
};

export default ResourcesPage;
